import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppStore } from '@/app/store';
import './NotificationToast.styles.css';

export const NotificationToast: React.FC = () => {
  const navigate = useNavigate();
  const notifications = useAppStore(state => state.notifications);
  const removeNotification = useAppStore(state => state.removeNotification);
  const [visibleNotifications, setVisibleNotifications] = useState<string[]>([]);
  const timeoutsRef = useRef<Map<string, NodeJS.Timeout>>(new Map());

  const handleNotificationClick = (notification: typeof notifications[0]) => {
    if (notification.link) {
      // Clear the auto-remove timeout
      if (timeoutsRef.current.has(notification.id)) {
        clearTimeout(timeoutsRef.current.get(notification.id));
        timeoutsRef.current.delete(notification.id);
      }
      // Remove the notification
      removeNotification(notification.id);
      setVisibleNotifications(prev => prev.filter(id => id !== notification.id));
      // Navigate to the link
      navigate(notification.link);
    }
  };

  useEffect(() => {
    // Track visible notifications
    const currentIds = notifications.map(n => n.id);

    setVisibleNotifications(prev => {
      // Add new notifications
      const newIds = currentIds.filter(id => !prev.includes(id));
      return [...prev, ...newIds];
    });

    // Auto-remove notifications after 10 seconds (10000ms)
    notifications.forEach(notification => {
      // Only set timeout if we haven't already set one for this notification
      if (!timeoutsRef.current.has(notification.id)) {
        const timeout = setTimeout(() => {
          removeNotification(notification.id);
          setVisibleNotifications(prev => prev.filter(id => id !== notification.id));
          timeoutsRef.current.delete(notification.id);
        }, 10000); // 10 seconds

        timeoutsRef.current.set(notification.id, timeout);
      }
    });

    // Cleanup timeouts for removed notifications
    return () => {
      const currentNotificationIds = new Set(notifications.map(n => n.id));
      timeoutsRef.current.forEach((timeout, id) => {
        if (!currentNotificationIds.has(id)) {
          clearTimeout(timeout);
          timeoutsRef.current.delete(id);
        }
      });
    };
  }, [notifications, removeNotification]);

  const getNotificationClass = (type: string) => {
    switch (type) {
      case 'success': return 'notification-success';
      case 'error': return 'notification-error';
      case 'warning': return 'notification-warning';
      case 'info': return 'notification-info';
      default: return '';
    }
  };

  const getIcon = (type: string) => {
    switch (type) {
      case 'success':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M20 6L9 17l-5-5" />
          </svg>
        );
      case 'error':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <line x1="15" y1="9" x2="9" y2="15" />
            <line x1="9" y1="9" x2="15" y2="15" />
          </svg>
        );
      case 'warning':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
        );
      case 'info':
      default:
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="16" x2="12" y2="12" />
            <line x1="12" y1="8" x2="12.01" y2="8" />
          </svg>
        );
    }
  };

  if (notifications.length === 0) return null;

  return (
    <div className="notification-container">
      {notifications.map(notification => (
        <div
          key={notification.id}
          className={`notification-toast ${getNotificationClass(notification.type)} ${
            visibleNotifications.includes(notification.id) ? 'notification-enter' : 'notification-exit'
          } ${notification.link ? 'notification-clickable' : ''}`}
          onClick={() => handleNotificationClick(notification)}
          role={notification.link ? 'button' : undefined}
          tabIndex={notification.link ? 0 : undefined}
        >
          <div className="notification-icon">
            {getIcon(notification.type)}
          </div>
          <div className="notification-content">
            <div className="notification-title">{notification.title}</div>
            {notification.message && (
              <div className="notification-message">{notification.message}</div>
            )}
          </div>
          <button
            className="notification-close"
            onClick={(e) => {
              e.stopPropagation(); // Prevent triggering the notification click
              // Clear the auto-remove timeout when manually dismissed
              if (timeoutsRef.current.has(notification.id)) {
                clearTimeout(timeoutsRef.current.get(notification.id));
                timeoutsRef.current.delete(notification.id);
              }
              removeNotification(notification.id);
              setVisibleNotifications(prev => prev.filter(id => id !== notification.id));
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      ))}
    </div>
  );
};